import { Silhouette } from '@pcln/brand'
import { themeGet } from '@styled-system/theme-get'
import { Absolute, Box, Container, Flex, Hide, Image, Layout, Relative, Text } from 'pcln-design-system'
import React from 'react'
import styled from 'styled-components'
import { StyledHeroImage } from '../HeroModule.style'
import SponsoredBy from '../SponsoredBy/SponsoredBy'
import { getHeroHalfOverlineImageMaxHeights } from '../utils/utils'

const HeroContainer = styled(Box)`
  z-index: -1;
  position: ${(props) => (props.isToutAvailable ? 'absolute' : 'relative')};
`

const StyledHero = styled(StyledHeroImage)`
  background-position: ${(props) => props.imageBackgroundFocus};
`

const StyledOverlineImage = styled(Image)`
  max-height: ${(props) => getHeroHalfOverlineImageMaxHeights(props.maxHeight)[0]};
  ${themeGet('mediaQueries.lg')} {
    max-height: ${(props) => getHeroHalfOverlineImageMaxHeights(props.maxHeight)[1]};
  }
`

const RenderHeroImage = ({
  shouldDisplaySilhouette,
  imageBorderRadius = '2xl',
  rounded,
  backgroundImageUrl,
  imageGradient,
  imageClassName,
  handleImageClick,
  ariaLabel,
  imageBackgroundFocus,
  isDarkerFill,
}) => {
  return shouldDisplaySilhouette ? (
    <Flex justifyContent='flex-end' data-testid='hero-silhouette'>
      <Relative height='100%' width={[1, null, null, null, 1 / 2]}>
        <Silhouette
          mirror='right'
          isDarkerFill={isDarkerFill}
          imageUrl={backgroundImageUrl}
          imageClassName={imageClassName}
          handleImageClick={handleImageClick}
          ariaLabel={ariaLabel}
        />
      </Relative>
    </Flex>
  ) : (
    <Flex justifyContent='flex-end' data-testid='regular-hero'>
      <Absolute
        boxShadowSize={['sm', null, 'lg']}
        borderRadius={imageBorderRadius}
        rounded={rounded}
        mt={1}
        height='100%'
        width={[1, null, null, null, 1 / 2]}
        className={imageClassName}
      >
        <StyledHero
          borderRadius={imageBorderRadius}
          borderWidth={0}
          my={[2, null, null, 0]}
          image={backgroundImageUrl}
          imageGradient={imageGradient}
          height='100%'
          width={1}
          imageBackgroundFocus={imageBackgroundFocus}
        />
      </Absolute>
    </Flex>
  )
}
function HalfHeroModuleWithForm(props) {
  const {
    heading,
    subheading,
    backgroundImage,
    overline,
    overlineImage,
    overlineImageHeightDisplayOptions,
    overlineImagePosition,
    imageGradient,
    sponsoredByImage,
    searchForms,
    imageBorderRadius,
    companionTout,
    rounded,
    shouldDisplaySilhouette = false,
    imageClassName,
    handleImageClick,
    ariaLabel,
    textColor = 'text.dark',
    imageBackgroundFocus = 'center',
    isDarkerFill = false,
  } = props

  const isToutAvailable = companionTout ? true : false
  const layout = [
    <Box key={0} zIndex={2}>
      <Flex
        mb={4}
        width={[1, null, null, null, 2 / 3]}
        flexDirection={overlineImagePosition === 'bottom' ? 'column-reverse' : 'column'}
        style={{ gap: '24px' }}
      >
        {overlineImage?.url && (
          <Box>
            <StyledOverlineImage
              alt={overlineImage.altText}
              src={overlineImage.url}
              maxHeight={overlineImageHeightDisplayOptions}
            />
          </Box>
        )}
        <Box>
          {overline && (
            <Text textStyle='overline' color='text.light' mb={2}>
              {overline}
            </Text>
          )}
          <Hide xl xxl>
            {heading && (
              <Text textStyle='heading2' color={textColor} mb={2} data-testid='search-form-heading'>
                {heading}
              </Text>
            )}
            {subheading && (
              <Text textStyle='subheading4' color={textColor}>
                {subheading}
              </Text>
            )}
          </Hide>
          <Hide xs sm md lg>
            {heading && (
              <Text textStyle='title3' color={textColor} mb={2}>
                {heading}
              </Text>
            )}
            {subheading && (
              <Text textStyle='subheading3' color={textColor}>
                {subheading}
              </Text>
            )}
          </Hide>
          {sponsoredByImage?.url && (
            <Flex mt={[3, null, null, 4]}>{<SponsoredBy {...sponsoredByImage} />}</Flex>
          )}
        </Box>
      </Flex>
      <Box mb={shouldDisplaySilhouette ? [0, null, null, null, 4] : 4}> {searchForms} </Box>
    </Box>,
    <Relative key={1} mt={['-8px', null, null, null, 0]}>
      <Hide xl xxl>
        <HeroContainer isToutAvailable={isToutAvailable} width={1} height='100%'>
          <RenderHeroImage
            imageBorderRadius={imageBorderRadius}
            rounded={rounded}
            imageGradient={imageGradient}
            backgroundImageUrl={backgroundImage.url}
            shouldDisplaySilhouette={shouldDisplaySilhouette}
            zIndex='0'
            imageClassName={imageClassName}
            handleImageClick={handleImageClick}
            ariaLabel={ariaLabel}
            imageBackgroundFocus={imageBackgroundFocus}
            isDarkerFill={isDarkerFill}
          />
        </HeroContainer>
        {isToutAvailable && (
          <Hide xl xxl margin='auto'>
            <Box p={4}>{companionTout}</Box>
          </Hide>
        )}
      </Hide>
      <Box>
        {isToutAvailable && (
          <Hide xs sm md lg margin='auto'>
            <Box pb={4} pt='50px'>
              {companionTout}
            </Box>
          </Hide>
        )}
      </Box>
    </Relative>,
  ]

  return (
    <Container pb={4} size='xl'>
      <Relative width={1} height='100%'>
        <Hide xs sm md lg>
          <Absolute zIndex={0} width={1} height='100%'>
            <RenderHeroImage
              imageBorderRadius={imageBorderRadius}
              rounded={rounded}
              imageGradient={imageGradient}
              backgroundImageUrl={backgroundImage.url}
              shouldDisplaySilhouette={shouldDisplaySilhouette}
              imageClassName={imageClassName}
              handleImageClick={handleImageClick}
              imageBackgroundFocus={imageBackgroundFocus}
              isDarkerFill={isDarkerFill}
            />
          </Absolute>
        </Hide>
        <Layout
          variation={['100', null, null, null, '66-33']}
          gap={[null, null, null, null, 'md']}
          rowGap={['md', null, 'sm']}
        >
          {layout}
        </Layout>
      </Relative>
    </Container>
  )
}

export default HalfHeroModuleWithForm
